/* Importing the necessary libraries and files */
import React, { memo, useCallback } from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';

/* Importing components from the @mui/material package. */
import { Box, Grid, Tooltip, Typography } from '@mui/material';
import { HelpOutline as HelpOutlineIcon } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers';

/* Importing components. */
import QuestionnaireAttachment from '../../components/QuestionnaireAttachment';
import QuestionnaireChoice from '../../components/QuestionnaireChoice';
import QuestionnaireChoiceMultiple from '../../components/QuestionnaireChoiceMultiple';
import QuestionnaireTextField from '../../components/QuestionnaireTextField';

const MemoizedQuestionnaireAttachment = memo(QuestionnaireAttachment);
const MemoizedQuestionnaireChoice = memo(QuestionnaireChoice);
const MemoizedQuestionnaireChoiceMultiple = memo(QuestionnaireChoiceMultiple);
const MemoizedQuestionnaireTextField = memo(QuestionnaireTextField);

RenderQuestionnaireForm.propTypes = {
  item: PropTypes.array,
  setFieldValue: PropTypes.func,
  values: PropTypes.object.isRequired,
  readOnly: PropTypes.bool,
};

/**
 * Renders a questionnaire form based on the provided item data.
 * @param {Object} props - The component props.
 * @param {Object} props.item - The questionnaire item data.
 * @param {Function} props.setFieldValue - The function to set form field values.
 * @param {Object} props.values - The current form field values.
 * @param {boolean} props.readOnly - Indicates if the form is read-only.
 * @returns {JSX.Element} The rendered questionnaire form.
 */
export default function RenderQuestionnaireForm(props) {
  const { item, setFieldValue, values, readOnly } = props;

  const memoizedSetFieldValue = useCallback(
    (id, value) => {
      setFieldValue(id, value);
    },
    [setFieldValue]
  );

  const renderElementDisplay = useCallback((element) => {
    if (!element) return null;

    if (element.extension) {
      return element.extension?.map((el, index) => {
        if (
          el.url === 'http://hl7.org/fhir/StructureDefinition/questionnaire-itemControl' &&
          el.valueCodeableConcept?.coding[0].code === 'help'
        ) {
          return (
            <Tooltip title={element.text} arrow sx={{ ml: 2 }} key={`${element.linkId}-${index}`}>
              <HelpOutlineIcon />
            </Tooltip>
          );
        }
        return (
          <Typography variant="subtitle2" sx={{ pt: 1 }} id={element.linkId} key={`${element.linkId}-${index}`}>
            {element.text}
          </Typography>
        );
      });
    }

    return (
      <Typography variant="subtitle2" sx={{ pt: 1 }} id={element.linkId}>
        {element.text}
      </Typography>
    );
  }, []);

  const renderElement = useCallback(
    (element, layer) => {
      if (!element) return null;
      if (element.type === 'group') {
        let variant = 'subtitle1';
        switch (layer) {
          case 0:
            variant = 'h4';
            break;
          case 1:
            variant = 'h5';
            break;
          case 2:
            variant = 'h6';
            break;
          default:
            variant = 'subtitle1';
        }

        return (
          <Grid item xs={12} key={element.linkId}>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', width: '100%', flexDirection: 'row', alignItems: 'center' }}>
                <Typography variant={variant}>{element.text}</Typography>
                {element.item
                  ?.filter((el) => el.type === 'display')
                  .map((el, index) => (
                    <React.Fragment key={`${element.linkId}-disp-${index}`}>{renderElementDisplay(el)}</React.Fragment>
                  ))}
              </Box>
            </Grid>
            <Grid container item xs={12} spacing={2} key={`${element.linkId}-children`}>
              {element.item
                ?.filter((el) => el.type !== 'display')
                .map((el, index) => (
                  <Grid item xs={12} key={`${element.linkId}-${index}`}>
                    {renderElement(el, layer + 1)}
                  </Grid>
                ))}
            </Grid>
          </Grid>
        );
      }
      if (
        element.type === 'string' ||
        element.type === 'text' ||
        element.type === 'decimal' ||
        element.type === 'integer'
      ) {
        return (
          <Box sx={{ display: 'flex', width: '100%', flexDirection: 'row', alignItems: 'center' }} key={element.linkId}>
            <MemoizedQuestionnaireTextField
              element={element}
              setFieldValue={memoizedSetFieldValue}
              values={values}
              readOnly={readOnly}
            />
            {element.item?.filter((el) => el.type === 'display').map((el) => renderElementDisplay(el))}
          </Box>
        );
      }
      if (element.type === 'choice') {
        if (element?.repeats) {
          return (
            <Box
              sx={{ display: 'flex', width: '100%', flexDirection: 'row', alignItems: 'center' }}
              key={element.linkId}
            >
              <MemoizedQuestionnaireChoiceMultiple
                element={element}
                setFieldValue={memoizedSetFieldValue}
                values={values}
                readOnly={readOnly}
              />
              {element.item?.filter((el) => el.type === 'display').map((el) => renderElementDisplay(el))}
            </Box>
          );
        }
        return (
          <Box sx={{ display: 'flex', width: '100%', flexDirection: 'row', alignItems: 'center' }} key={element.linkId}>
            <MemoizedQuestionnaireChoice
              element={element}
              setFieldValue={memoizedSetFieldValue}
              values={values}
              readOnly={readOnly}
            />
            {element.item?.filter((el) => el.type === 'display').map((el) => renderElementDisplay(el))}
          </Box>
        );
      }
      if (element.type === 'date') {
        return (
          <DatePicker
            readOnly={readOnly}
            label={element.required ? `${element.text}*` : element.text}
            value={values[element.linkId] ? moment(values[element.linkId]) : null}
            onChange={(value) => {
              memoizedSetFieldValue(
                element.linkId,
                value && moment.isMoment(value) && value.isValid() ? moment(value).format('YYYY-MM-DD') : ''
              );
            }}
            slotProps={{ textField: { fullWidth: true, variant: readOnly ? 'standard' : 'outlined' } }}
            key={element.linkId}
          />
        );
      }
      if (element.type === 'attachment') {
        return (
          <Box sx={{ display: 'flex', width: '100%', flexDirection: 'row', alignItems: 'center' }} key={element.linkId}>
            <MemoizedQuestionnaireAttachment
              element={element}
              setFieldValue={memoizedSetFieldValue}
              values={values}
              readOnly={readOnly}
            />
            {element.item?.filter((el) => el.type === 'display').map((el) => renderElementDisplay(el))}
          </Box>
        );
      }
      return null;
    },
    [memoizedSetFieldValue, readOnly, renderElementDisplay, values]
  );

  return <>{item?.map((el) => renderElement(el, 0))}</>;
}
